import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  zeroize,
  wrapKeyWithPrf,
  generatePrfSalt,
} from '@gonka-wallet/crypto';
import {
  openWalletDb,
  saveWallet,
  type EncryptedWalletBlob,
} from '@gonka-wallet/storage';
import { useWalletStore } from '../stores/walletStore.js';
import { bytesToHex } from '../lib/format.js';
import { enrollBiometric } from '../lib/webauthn.js';

export interface BiometricOfferModalProps {
  /** The freshly created/imported/unlocked wallet to bind biometrics to. */
  readonly wallet: EncryptedWalletBlob;
  /** The Argon2id-derived seed-encryption key. The modal owns this
   *  buffer for the duration of its lifecycle and zeroizes it on
   *  every exit path. */
  readonly derivedKey: Uint8Array;
  /** Fires after either Enable (success) or Not now. */
  readonly onDone: () => void;
}

/**
 * "Use Face ID next time?" iOS-style enrollment offer.
 *
 * Used immediately after Create / Import / first PIN unlock so the
 * user gets one chance to opt into biometric unlock without having
 * to navigate to Settings or re-enter their PIN. Once dismissed the
 * settings flag `biometricPromptDismissed` is set so we never nag
 * again — re-enabling later is done from Settings.
 */
export function BiometricOfferModal({
  wallet,
  derivedKey,
  onDone,
}: BiometricOfferModalProps) {
  const { t } = useTranslation();
  const wallets = useWalletStore((s) => s.wallets);
  const setWallets = useWalletStore((s) => s.setWallets);
  const [busy, setBusy] = useState(false);

  async function accept() {
    if (busy) return;
    setBusy(true);
    let prfSecret: Uint8Array = new Uint8Array(0);
    try {
      const prfSalt = generatePrfSalt();
      const enrollment = await enrollBiometric(
        wallet.id,
        wallet.name,
        prfSalt,
      );
      prfSecret = enrollment.prfSecret;
      const wrappedKey = await wrapKeyWithPrf(derivedKey, prfSecret);
      const updated: EncryptedWalletBlob = {
        ...wallet,
        biometricWrap: {
          credentialId: enrollment.credentialId,
          prfSalt: bytesToHex(prfSalt),
          wrappedKey: bytesToHex(wrappedKey),
        },
      };
      const db = await openWalletDb();
      await saveWallet(db, updated);
      // Some callers (Create, Import) already have the wallet in the
      // store; some (Unlock) modify an existing entry. Replace by id.
      const next = wallets.some((w) => w.id === updated.id)
        ? wallets.map((w) => (w.id === updated.id ? updated : w))
        : [...wallets, updated];
      setWallets(next);
      finish();
    } catch (e) {
      const msg = (e as Error).message ?? '';
      if (
        msg.toLowerCase().includes('cancel') ||
        msg.toLowerCase().includes('not allowed')
      ) {
        // User cancelled the Face ID prompt — leave the modal open so
        // they can retry or pick "Not now". Don't zeroize yet.
        setBusy(false);
        return;
      }
      // Hard error (PRF unsupported, etc) — bail out as if dismissed.
      finish();
    } finally {
      zeroize(prfSecret);
    }
  }

  function dismiss() {
    if (busy) return;
    // "Not now" is per-session only. We do NOT persist a dismissal
    // flag — if the user didn't enroll, we'll show the offer again
    // on the next successful PIN unlock. Matches the "OS prompts
    // every login until you enroll" pattern.
    finish();
  }

  function finish() {
    zeroize(derivedKey);
    onDone();
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/70 p-4 sm:items-center">
      <div className="w-full max-w-md rounded-2xl bg-surface-1 p-6">
        <h2 className="mb-2 text-lg font-semibold">{t('biometric.offerTitle')}</h2>
        <p className="mb-5 text-sm text-text-secondary">
          {t('biometric.offerDescription')}
        </p>
        <div className="flex flex-col gap-2">
          <button
            onClick={() => void accept()}
            disabled={busy}
            className="rounded-xl bg-white px-4 py-3 font-medium text-black disabled:opacity-50"
          >
            {busy ? t('biometric.prompt') : t('biometric.offerAccept')}
          </button>
          <button
            onClick={dismiss}
            disabled={busy}
            className="rounded-xl border border-border-hairline bg-transparent px-4 py-3 text-text-primary disabled:opacity-50"
          >
            {t('biometric.offerLater')}
          </button>
        </div>
      </div>
    </div>
  );
}
